// ---------------------------------------------------------------------------
// B16: Trial Management — track trial status + conversion funnel
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

export interface TrialStatus {
  isTrialing: boolean;
  trialPlan: string;
  trialStartedAt: string;
  trialEndsAt: string;
  daysRemaining: number;
  daysElapsed: number;
  totalDays: number;
  usagePercentage: number; // 0-100 how much of trial features used
  hasPaymentMethod: boolean;
  warningLevel: "none" | "info" | "warning" | "urgent";
}

export function useTrialStatus() {
  const [trial, setTrial] = useState<TrialStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ data: TrialStatus }>("/api/billing/trial-status")
      .then((res) => setTrial(res.data))
      .catch(() => setTrial(null))
      .finally(() => setIsLoading(false));
  }, []);

  return { trial, isLoading };
}

export function useTrialConversion() {
  const [isConverting, setIsConverting] = useState(false);

  const startCheckout = useCallback(async (planSlug: string) => {
    setIsConverting(true);
    try {
      const res = await apiFetch<{ checkoutUrl: string }>("/api/billing/upgrade", {
        method: "POST",
        body: JSON.stringify({ planSlug }),
      });
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
      }
    } catch {
      // Checkout failed — non-critical, user can retry
    } finally {
      setIsConverting(false);
    }
  }, []);

  const extendTrial = useCallback(async (reason: string) => {
    try {
      await apiFetch("/api/billing/trial/extend", {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
    } catch {
      // Extension failed
    }
  }, []);

  return { startCheckout, extendTrial, isConverting };
}
